import z from "zod";

import type { UUID } from "@thalamos/common";
import { notificationChannelFns as thalamosNotificationChannelFns } from "@thalamos/common";

import type { SupportedIncidentType } from "./incidentType.js";
import {
  type IncidentWorkItemId,
  type IncidentWorkItemIdUnbranded,
} from "./incidentWorkItem.js";

export const NotificationEventValues = ["resource-reload"] as const;
export type NotificationEvent = (typeof NotificationEventValues)[number];
export const NotificationEventSchema = z.enum({
  resourceReload: "resource-reload",
} satisfies Record<string, NotificationEvent>);

export type ResourceReloadEvent = {
  type: "resource-reload";
};

export type BrowserNotificationEvent = ResourceReloadEvent;

export const NotificationBodyTypeValues = [
  "transfer-requested",
  "transfer-rejected",
  "s136-form-accepted",
  "s136-form-rejected",
  "police-presence",
] as const;
export type NotificationBodyType = (typeof NotificationBodyTypeValues)[number];
export const NotificationBodyTypeSchema = z.enum({
  transferRequested: "transfer-requested",
  transferRejected: "transfer-rejected",
  s136FormAccepted: "s136-form-accepted",
  s136FormRejected: "s136-form-rejected",
  policePresenceRequested: "police-presence",
} satisfies Record<string, NotificationBodyType>);

export type TransferRequestedBody = {
  type: "transfer-requested";
};

export type TransferRejectedBody = {
  type: "transfer-rejected";
};

export type S136FormAcceptedBody = {
  type: "s136-form-accepted";
  /**
   * Absent where the incident's triage selection was missing or not a
   * `SupportedIncidentType` when accepted, which includes notifications
   * recorded before this field existed.
   */
  incidentType?: SupportedIncidentType;
};

export type PolicePresenceRequestedBody = {
  type: "police-presence";
  incidentWorkItemId: string;
  formId: string;
};

export type S136FormRejectedBody = {
  type: "s136-form-rejected";
  incidentWorkItemId: string;
};

export type ThalamosNotification = {
  id: UUID;
  text:
    | TransferRequestedBody
    | TransferRejectedBody
    | S136FormAcceptedBody
    | S136FormRejectedBody
    | ResourceReloadEvent
    | PolicePresenceRequestedBody;
  status: "new" | "read" | "discarded";
  incidentWorkItemId: IncidentWorkItemId;
};

export type ThalamosNotificationUnbranded = {
  id: UUID;
  text:
    | TransferRequestedBody
    | TransferRejectedBody
    | S136FormAcceptedBody
    | S136FormRejectedBody
    | ResourceReloadEvent
    | PolicePresenceRequestedBody;
  status: "new" | "read" | "discarded";
  incidentWorkItemId: IncidentWorkItemIdUnbranded;
};

export const notificationChannelFns = {
  user: {
    notifications: (userId: string) => `user.${userId}.notifications`,
  },
  ...thalamosNotificationChannelFns,
};
